<script setup lang="ts">
import type { Ref } from 'vue'
import Backdrop from '@ui/components/backdrop/Backdrop.vue'
import DrawerLoader from '@ui/components/drawer/DrawerLoader.vue'
import IconButton from '@ui/components/icon/IconButton.vue'
import { ButtonVariant, Sizes } from '@ui/styles'
import { Placements } from '@ui/utils/placements'
import { useOverlayScrollLock } from '@ui/utils/useOverlayScrollLock'
import { useOverlayStack } from '@ui/utils/useOverlayStack'
import { useEventListener } from '@vueuse/core'
import { Motion, useDomRef, useReducedMotion, useSpring } from 'motion-v'
import Times from '~icons/cue-regular/times'
import { useBottomSheetDrag } from './useBottomSheetDrag'

type SnapPoint = number | `${number}%`

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    label?: string
    description?: string
    closable?: boolean
    /** A single resting height. Additional entries are ignored. */
    snapPoints?: ReadonlyArray<SnapPoint>
    pending?: boolean
    headerAlign?: 'left' | 'center'
    padding?: boolean
    divider?: boolean
  }>(),
  {
    label: '',
    description: '',
    closable: true,
    pending: false,
    headerAlign: 'left',
    padding: true,
    divider: false,
  },
)

defineSlots<{
  default?: (props: { close: () => void }) => void
  actions?: (props: { close: () => void }) => void
}>()

const show = defineModel<boolean>('show', { default: false })

const placement = defineModel<string>('placement', { default: Placements.Bottom })

const sheet: Ref<HTMLElement | null> = useDomRef()
const titleId = useId()
const descriptionId = useId()
const { index, active, isTopmost, activate, deactivate } = useOverlayStack()
const reducedMotion = useReducedMotion()
const duration = computed(() => (reducedMotion.value ? 0 : 300))
const springOffset = useSpring(0, { stiffness: 600, damping: 48, mass: 0.9 })
const visualViewport = shallowRef<VisualViewport>()
const viewportHeight = ref(0)
const viewportBottom = ref(0)
let returnFocus: HTMLElement | null = null

function close() {
  show.value = false
}

const { offset, dragging, reset, onPointerDown, onPointerMove, onPointerUp, onPointerCancel } = useBottomSheetDrag(
  sheet,
  computed(() => show.value && isTopmost.value),
  close,
  () => springOffset.get(),
)
watch(
  [offset, dragging, reducedMotion],
  ([value, isDragging, reduce]) => {
    if (isDragging || reduce) {
      springOffset.jump(value)
    } else {
      springOffset.set(value)
    }
  },
  { flush: 'sync' },
)
useOverlayScrollLock(active)

function updateViewport() {
  const viewport = visualViewport.value
  viewportHeight.value = viewport?.height ?? window.innerHeight
  viewportBottom.value = Math.max(0, window.innerHeight - viewportHeight.value - (viewport?.offsetTop ?? 0))
}

useEventListener(visualViewport, ['resize', 'scroll'], updateViewport)
useEventListener('resize', updateViewport)

const sheetStyle = computed(() => {
  const requestedHeight = props.snapPoints?.[0]
  const height =
    typeof requestedHeight === 'number'
      ? `${Math.max(0, requestedHeight)}px`
      : requestedHeight && viewportHeight.value
        ? `${(Math.max(0, Number.parseFloat(requestedHeight)) / 100) * viewportHeight.value}px`
        : 'auto'

  return {
    '--bottom-sheet-height': height,
    '--bottom-sheet-max-height': viewportHeight.value ? `${viewportHeight.value * 0.95}px` : '95dvh',
    '--bottom-sheet-bottom': `${viewportBottom.value}px`,
    '--bottom-sheet-safe-bottom': viewportBottom.value > 0 ? '0px' : 'env(safe-area-inset-bottom, 0px)',
    '--bottom-sheet-duration': `${duration.value}ms`,
  }
})

onMounted(() => {
  visualViewport.value = window.visualViewport ?? undefined
  updateViewport()
  watch(
    show,
    value => {
      if (value) {
        if (!active.value) {
          returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
        }
        reset()
        springOffset.jump(0)
        activate()
      }
    },
    { immediate: true },
  )
})

function onAfterLeave() {
  if (!show.value) {
    reset()
    springOffset.jump(0)
    releaseLayer()
  }
}

async function releaseLayer() {
  const shouldRestoreFocus = isTopmost.value
  deactivate()
  await nextTick()
  if (shouldRestoreFocus && returnFocus?.isConnected && !returnFocus.closest('[inert]')) {
    returnFocus.focus({ preventScroll: true })
  }
}

onBeforeUnmount(() => {
  springOffset.stop()
  releaseLayer()
})

watch(
  [sheet, show],
  async () => {
    await nextTick()
    if (isTopmost.value && show.value && !sheet.value?.contains(document.activeElement)) {
      sheet.value?.focus({ preventScroll: true })
    }
  },
  { flush: 'post' },
)

useEventListener(
  'keydown',
  (event: KeyboardEvent) => {
    if (!isTopmost.value) {
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopImmediatePropagation()
      close()
    } else if (event.key === 'Tab') {
      const panel = sheet.value
      const focusable = Array.from(
        panel?.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex]') ?? [],
      ).filter(
        element =>
          element.tabIndex >= 0
          && !element.matches(':disabled')
          && !element.closest('[inert]')
          && element.getClientRects().length > 0
          && getComputedStyle(element).visibility !== 'hidden',
      )
      const first = focusable[0]
      const last = focusable.at(-1)
      const current = document.activeElement

      if (
        !first
        || !panel?.contains(current)
        || current === panel
        || (event.shiftKey ? current === first : current === last)
      ) {
        event.preventDefault()
        const target = event.shiftKey ? (last ?? panel) : (first ?? panel)
        target?.focus({ preventScroll: true })
      }
    }
  },
  { capture: true },
)

placement.value = Placements.Bottom
</script>

<template lang="pug">
Teleport(to="#bottom-sheet" defer)
  .bottom-sheet-layer.position-fixed.t-0.b-0.l-0.r-0(
    v-show="active"
    :style="{ zIndex: index }"
    :inert="!isTopmost"
    :aria-hidden="!isTopmost || undefined"
  )
    Backdrop(v-model:show="show" teleportDisabled :zIndex="0" :lockScroll="false")
    Transition(name="bottom-sheet" :duration="duration" @afterLeave="onAfterLeave")
      .bottom-sheet-position.position-fixed.l-0.r-0.m-x-auto.w-100p.max-w-800.z-index-1(v-if="show && active" :style="sheetStyle")
        Motion.bottom-sheet.flex.col-0.position-relative.overflow-hidden(
          v-bind="$attrs"
          ref="sheet"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="label ? titleId : undefined"
          :aria-describedby="description ? descriptionId : undefined"
          tabindex="-1"
          :style="{ ...sheetStyle, y: springOffset }"
        )
          .header.flex.col-0.flex-shrink-0.p-t-20(
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerCancel"
            @lostpointercapture="onPointerCancel"
          )
            .flex.col-4.p-0-20-16(v-if="closable || label || description")
              IconButton.icon.m-l-auto(v-if="closable" :name="Times" :size="Sizes.Luke" :variant="ButtonVariant.Function" @click="close")
              .flex.col-2(v-if="label || description" :style="{ 'text-align': headerAlign }")
                span.label.typ-18-26-600(v-if="label" :id="titleId") {{ $t(label) }}
                span.description.typ-16-24-400(v-if="description" :id="descriptionId") {{ $t(description) }}
          .content.flex-1.min-h-0.overflow-y-auto(:class="{ 'p-0-20-16': padding }")
            DrawerLoader(v-if="pending")
            slot(:close="close")
          .actions.flex.row-16.flex-shrink-0.p-20(v-if="$slots.actions" :class="{ 'actions--divided': divider }")
            slot(name="actions" :close="close")
</template>

<style scoped>
.bottom-sheet-layer {
  pointer-events: none;
}

.bottom-sheet-layer :deep(.backdrop) {
  pointer-events: auto;
}

.bottom-sheet {
  height: var(--bottom-sheet-height);
  max-height: var(--bottom-sheet-max-height);
  padding-bottom: var(--bottom-sheet-safe-bottom);
  border-radius: 24px 24px 0 0;
  background: var(--cue-bg-surface-highest);
  box-shadow: 0 -5px 60px color-mix(in srgb, var(--cue-static-neutral-inverse) 20%, transparent);
  pointer-events: auto;
  outline: none;
}

.bottom-sheet-position {
  bottom: var(--bottom-sheet-bottom);
  transition: transform var(--bottom-sheet-duration) ease;
}

.bottom-sheet-enter-from,
.bottom-sheet-leave-to {
  transform: translateY(100%);
}

.header {
  touch-action: none;
  user-select: none;
}

.icon {
  color: var(--cue-content-neutral-tertiary);
}

.label {
  color: var(--cue-content-neutral-primary);
}

.description {
  color: var(--cue-content-neutral-secondary);
}

.content {
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.actions--divided {
  border-top: 1px solid var(--cue-border-neutral-secondary);
}
</style>
